import React, { useEffect, useState } from 'react';

import { Trans } from '@lingui/react/macro';
import {
  CheckCircleOutlined,
  ImageNotSupported,
  NotStartedOutlined,
} from '@mui/icons-material';
import { faArrowLeft, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LinearProgress } from '@mui/material';
import type { UseQueryResult } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { Redirect } from 'react-router';
import { Link } from 'react-router-dom';

import {
  HISTORICAL_ANALYSIS_RETRIES,
  useEnqueuePatientAnalysisHistorical,
  usePatientAnalysisHistoricalProgress,
} from '../../react-query/usePatientAnalysisHistorical';
import { useAppContext } from '../../utils/hooks/useAppContext';
import { unifiedIdAtom } from '../patientList/atoms';

import { ProcessingHistoricalContainer } from './styles';

export const HistoricalImagesContainer = (props: React.PropsWithChildren) => (
  <ProcessingHistoricalContainer>
    {props.children}
  </ProcessingHistoricalContainer>
);

export const ProcessingStatusContainer = (props: {
  status: string;
  message: string;
  icon: React.ReactNode;
  returnRoute: string;
}) => {
  const linkLabel =
    props.returnRoute === '/uploader' ? 'Huddle' : 'Findings Viewer';
  return (
    <ProcessingHistoricalContainer>
      <div className="message">
        <Link to={props.returnRoute}>
          <button className="close-button vai-p-2 vai-cursor-pointer vai-rounded">
            <FontAwesomeIcon icon={faTimes} className="vai-w-4 vai-h-4" />
          </button>
        </Link>
        <div className="title">
          <Trans>Checking for missing images</Trans>
        </div>
        <div className={`status ${props.status}`}>
          {props.icon}
          <span className="text">{props.message}</span>
        </div>
        <Link to={props.returnRoute}>
          <button className="back-button vai-bg-transparent vai-border-none vai-p-2 vai-cursor-pointer vai-rounded vai-flex vai-items-center vai-gap-2 hover:vai-bg-gray-100">
            <FontAwesomeIcon icon={faArrowLeft} className="vai-w-4 vai-h-4" />
            <span>{` Back to ${linkLabel}`}</span>
          </button>
        </Link>
      </div>
    </ProcessingHistoricalContainer>
  );
};

// this is a generic component that will display a message based on the status of the requests
// for checking if a patient has un-analyzed images, enqueuing the images for analysis, and
// checking the status of the analysis
export const ProcessingStatus = ({
  response,
  returnRoute,
}: {
  response: UseQueryResult;
  returnRoute: string;
}) => {
  if (!response) return null;
  const { data: responseData, isFetched, isError, error } = response;

  let isNonEmptyResponse = false;
  let hasNoHistoricalImages = false;
  let hasIncompleteHistoricalImages = false;
  let isHistoricalImagesDone = false;

  if (isFetched && Array.isArray(responseData)) {
    const [data] = responseData || [];
    const { data: historicalData } = data;
    isNonEmptyResponse =
      !!historicalData && Object.keys(historicalData).length > 0;
    hasNoHistoricalImages = !Array.isArray(historicalData) && !isError;

    hasIncompleteHistoricalImages = historicalData.some(
      (analysis: { status: string }) => analysis.status !== 'COMPLETE',
    );
    isHistoricalImagesDone =
      historicalData.length > 0 &&
      historicalData.every(
        (analysis: { status: string }) => analysis.status === 'COMPLETE',
      );
  }

  if (isFetched) {
    if (hasNoHistoricalImages) {
      // if the patient has no historical images the response is an empty data object
      return (
        <ProcessingStatusContainer
          status="noImages"
          icon={<ImageNotSupported />}
          message="No historical images found for this patient."
          returnRoute={returnRoute}
        />
      );
    } else if (isNonEmptyResponse && isError) {
      // if the patient has images but they can't be analyzed, the response is
      // a 404 error with a message
      return (
        <ProcessingStatusContainer
          status="noImagesAnalyze"
          icon={<NotStartedOutlined />}
          message="No un-analyzed historical images for this patient."
          returnRoute={returnRoute}
        />
      );
    } else if (!!error && !isNonEmptyResponse) {
      // if the request has an error of any kind other than those cases below display an error message
      return (
        <ProcessingStatusContainer
          status="error"
          icon={<NotStartedOutlined />}
          message="Error processing historical images"
          returnRoute={returnRoute}
        />
      );
    } else if (hasIncompleteHistoricalImages && !isHistoricalImagesDone) {
      // if the patient has images that are being analyzed, the response is an
      // array of objects with status of each analysis one of which is not 'COMPLETE'
      return (
        <ProcessingStatusContainer
          status="imagesFound"
          icon={<CheckCircleOutlined />}
          message="Historical images found, processing..."
          returnRoute={returnRoute}
        />
      );
    } else if (!hasIncompleteHistoricalImages && isHistoricalImagesDone) {
      // if the patient has images that have been analyzed, the response is an
      // array of objects with status of each analysis all of which are 'COMPLETE'
      return (
        <ProcessingStatusContainer
          status="imagesDone"
          icon={<CheckCircleOutlined />}
          message="All historical images have been analyzed"
          returnRoute={returnRoute}
        />
      );
    }
  }
  return (
    <ProcessingStatusContainer
      status="loading"
      icon={<LinearProgress />}
      message="Processing..."
      returnRoute={returnRoute}
    />
  );
};

// once a patient has been found to have un-analyzed images, check the status of the analysis
// by polling the server for the status of the analysis, then redirect to the annotator when
// the analysis is complete
export const HistoricalImagesProcessingStatus = ({
  unifiedId,
  returnRoute,
}: {
  unifiedId: string;
  returnRoute: string;
}) => {
  const { user } = useAppContext();
  const { primaryPracticeId, primaryOrganizationId } = user;
  const response = usePatientAnalysisHistoricalProgress({
    unifiedId,
    primaryPracticeId,
    primaryOrganizationId,
  });
  const [timer, setTimer] = useState(0);
  useEffect(() => {
    if (response && timer > 2) return;
    if (response) {
      setTimeout(() => {
        setTimer(timer + 1);
      }, 3000);
    }
  }, [response, timer]);

  if (!response) return null;
  const { data, isFetched, isError } = response;

  const isHistoricalImagesDone =
    isFetched &&
    Array.isArray(data) &&
    data.length > 0 &&
    data.every(
      (analysis: { status: string }) => analysis.status === 'COMPLETE',
    );
  const isHistoricalImagesOnHold =
    isFetched &&
    Array.isArray(data) &&
    data.length > 0 &&
    data.some((analysis: { status: string }) => analysis.status === 'ON_HOLD');
  const isHistoricalImagesRefetchExceeded =
    timer > HISTORICAL_ANALYSIS_RETRIES &&
    isFetched &&
    Array.isArray(data) &&
    data.length > 0 &&
    data.every(
      (analysis: { status: string }) => analysis.status !== 'COMPLETE',
    );
  if (isHistoricalImagesDone) {
    const patientId = data[0].patientId;
    return <Redirect to={`/annotator/${patientId}`} />;
  }
  if (isHistoricalImagesRefetchExceeded) {
    return (
      <ProcessingStatusContainer
        status="error"
        icon={<NotStartedOutlined />}
        message="Error processing historical images. Please contact support."
        returnRoute={returnRoute}
      />
    );
  }
  if (isHistoricalImagesOnHold) {
    return (
      <ProcessingStatusContainer
        status="imagesOnHold"
        icon={<NotStartedOutlined />}
        message="Historical images are on hold. Attempting to process..."
        returnRoute={returnRoute}
      />
    );
  }
  if (isError) {
    return (
      <ProcessingStatusContainer
        status="error"
        icon={<NotStartedOutlined />}
        message="No un-analyzed historical images for this patient."
        returnRoute={returnRoute}
      />
    );
  } else {
    return <ProcessingStatus response={response} returnRoute={returnRoute} />;
  }
};

// once a patient has be found to have un-analyzed images, enqueue the images and
// display a processing status if the request is complete, still in progress or has an error,
// otherwise render a component that will poll for the status of the analysis
export const EnqueueHistoricalImages = ({
  imagingPatientId,
  returnRoute,
}: {
  imagingPatientId: string;
  returnRoute: string;
}) => {
  const { user } = useAppContext();
  const unifiedId = useAtomValue(unifiedIdAtom);
  const { primaryPracticeId, primaryOrganizationId } = user;
  const response = useEnqueuePatientAnalysisHistorical(
    imagingPatientId,
    primaryPracticeId,
    primaryOrganizationId,
  );
  if (!response) return null;
  const { data: responseData, isFetched } = response;

  if (isFetched && Array.isArray(responseData)) {
    const [data] = responseData || [];
    const { data: historicalData } = data;
    const historicalImagesDone =
      historicalData.length > 0 &&
      historicalData.every(
        (analysis: { status: string }) => analysis.status === 'COMPLETE',
      );

    return (
      <>
        {!historicalImagesDone ? (
          <HistoricalImagesProcessingStatus
            unifiedId={unifiedId || ''}
            returnRoute={returnRoute}
          />
        ) : (
          <ProcessingStatus response={response} returnRoute={returnRoute} />
        )}
      </>
    );
  }
};
